// PREIS-/ABSCHLUSSBÜHNE (/abo, freigegeben 30.09.2026) — MASTER-BILD 1:1 + unsichtbare Technik.
//
// Betreiberfreigabe: das Design-PNG ist die verbindliche visuelle Wahrheit und wird 1:1
// als EIN Master-Bild eingebunden. KEIN Nachbau, KEINE Retusche, KEINE Textänderung.
// Die Angabe „90–130 € pro Stunde (netto)" bleibt bewusst 1:1 erhalten (Option C).
//
// Nur echte Funktion liegt als unsichtbare Technik über/relativ zum Bild:
//   1. Trial-CTA        → bestehender startTrial-Flow (demo-start-wizard)
//   2. PDF-Download      → echte Leistungsübersicht-PDF
//   3. Preisrechner      → nur die dynamischen Pixel: eingebrannte − / + bleiben Bild
//      (transparente Klick-Hotspots), nur Mitarbeiterzahl und Betriebspreis werden mit
//      farblich passenden Live-Patches überdeckt. Preisquelle AUSSCHLIESSLICH /api/catalog
//      (14,90 € × aktive Mitarbeiter) — nichts hartcodiert.
//
// Alle Positionen sind relativ (%) zum Master-Bild + cqw → skalieren mit ihm (Desktop/Mobile).
import { useEffect, useLayoutEffect, useRef, useState } from "react";
// Selbst gehostete Handschrift (Notizzettel) – NICHT über Google-CDN, nur latin 400/600.
import "@fontsource/caveat/latin-400.css";
import "@fontsource/caveat/latin-600.css";

const MASTER = "/abo/preis/abo-preis-abschluss-master-v2.webp";
const PDF = "/abo/preis/leistungsuebersicht-clartext.pdf";
const OUTFIT_HREF =
  "https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap";

// MOBIL-CTA-06, Betreiberfreigabe 27.10.2026 — Bildtexte wörtlich für den Handy-Block (Spalte 3 / Fußmerkmal 1 = HTML-Overlay unten)
const MOBIL_TITEL = "Was kostet Arbeitsschutz heute wirklich?";
const MOBIL_UNTERTITEL =
  "Nicht nur Geld – sondern vor allem Ihre Zeit, Ihre Nerven und ein stillstehender Betrieb.";
const MOBIL_KARTEN = [
  ["Ihre Zeit", "Planen, organisieren, Unterlagen suchen …"],
  ["Betriebsunterbrechung", "Mitarbeiter für Unterweisungen aus dem Arbeitsalltag holen …"],
  ["Externe Dokumente", "Gefährdungsbeurteilung, Betriebsanweisungen, Pläne … 90–130 € pro Stunde (netto)."],
  ["Ihre Nerven", "Fristen, Nachweise, BG-Prüfung … immer die Sorge: „Haben wir alles?“"],
];
const MOBIL_VERTRAUEN = [
  "Ohne Zahlungsdaten.",
  "Test endet nach 14 Tagen von selbst.",
  "Gefahrstoffe und Hautschutzplan inklusive.",
];
const MOBIL_MERKMALE = [
  "Orientiert an ArbSchG und DGUV",
  "Für kleine und größere Betriebe",
  "In der Sprache Ihrer Mitarbeiter",
  "Sofort einsatzbereit",
];

function ensureOutfit() {
  if (typeof document === "undefined") return;
  if (document.querySelector('link[data-clr-font="abo-preisstage-outfit"]')) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = OUTFIT_HREF;
  link.dataset.clrFont = "abo-preisstage-outfit";
  document.head.appendChild(link);
}

export default function AboPreisAbschlussStage({
  priceState,
  currencySymbol,
  monthlyText,
  employeeCount,
  setEmp,
  totalText,
  startTrial,
  trialLoading,
  trialError,
}) {
  useEffect(() => {
    ensureOutfit();
  }, []);
  const [empLeer, setEmpLeer] = useState(false); // MOBIL-CTA-03: Zahlenfeld darf beim Tippen kurz leer sein

  // Preis-Auto-Fit: 1–6 MA passen in den freien Raum vor dem gelben Zettel und bleiben
  // PIXELGENAU (Basis 5.95cqw). Erst wenn der dynamische Betrag (dreistellig) zu breit
  // wird, verkleinert sich AUSSCHLIESSLICH die Preisschrift proportional, bis die rechte
  // Kante mit Sicherheitsabstand vor der linken Kante des Zettels (48.83%) endet.
  // Master-Bild/Layout/Zettel bleiben unverändert. Längen-/containerbasiert (kein 10er-Sonderfall).
  const priceTextRef = useRef(null);
  useLayoutEffect(() => {
    const span = priceTextRef.current;
    if (!span) return;
    const frame = span.closest(".apa-frame");
    if (!frame) return;
    const fit = () => {
      const fw = frame.clientWidth;
      if (!fw) return;
      const availPx = (fw * (48.83 - 26.37 - 1.5)) / 100; // Raum Preis→Zettel, 1.5% Puffer
      span.style.fontSize = ""; // zurück auf Basis (5.95cqw)
      if (span.getBoundingClientRect().width <= availPx) return; // 1–6 MA: pixelgenau, kein Eingriff
      let fs = fw * 0.0595; // 5.95cqw in px
      let guard = 0;
      do {
        fs *= 0.96;
        span.style.fontSize = `${fs}px`;
        guard += 1;
      } while (span.getBoundingClientRect().width > availPx && guard < 40);
    };
    fit();
    const raf = requestAnimationFrame(fit);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fit).catch(() => {});
    }
    window.addEventListener("resize", fit);
    return () => {
      window.removeEventListener("resize", fit);
      cancelAnimationFrame(raf);
    };
  }, [priceState, totalText, currencySymbol, employeeCount]);

  return (
    <section className="apa-section" data-testid="abo-preis">
      <style>{`
        .apa-section{background:#e9eef4;padding:clamp(28px,6vw,72px) 16px;}
        .apa-frame{position:relative;container-type:inline-size;width:100%;max-width:1024px;margin:0 auto;box-shadow:0 24px 60px -28px rgba(15,40,90,.35);border-radius:14px;overflow:hidden;background:#fff;}
        .apa-master{display:block;width:100%;height:auto;-webkit-user-select:none;user-select:none;}

        /* Unsichtbare Klick-Hotspots (Hero-Standard) */
        .apa-hot{position:absolute;border:0;background:transparent;padding:0;margin:0;cursor:pointer;display:block;-webkit-tap-highlight-color:transparent;}
        .apa-hot:focus-visible{outline:3px solid #2f9e5b;outline-offset:2px;border-radius:10px;}
        .apa-hot[disabled]{cursor:default;}
        .apa-hot-trial{left:24.0%;top:75.3%;width:34.6%;height:4.6%;border-radius:999px;}
        .apa-hot-pdf{left:63.6%;top:80.7%;width:32.2%;height:7.0%;border-radius:16px;}
        .apa-step-minus{left:27.9%;top:61.0%;width:4.8%;height:4.6%;border-radius:999px;}
        .apa-step-plus{left:37.7%;top:61.0%;width:4.8%;height:4.6%;border-radius:999px;}

        /* Live-Zahlen: farblich passende Patches, decken exakt die eingebrannten Zahlen */
        .apa-num{position:absolute;display:flex;align-items:center;line-height:1;color:#022264;font-family:"Outfit",system-ui,sans-serif;pointer-events:none;white-space:nowrap;overflow:visible;}
        .apa-num-count{left:32.6%;top:61.4%;width:5.4%;height:3.6%;justify-content:center;background:#ebf3fd;font-weight:700;font-size:2.5cqw;}
        .apa-num-price{left:26.37%;top:66.4%;width:21.2%;height:7.0%;justify-content:flex-start;background:#ffffff;font-weight:800;letter-spacing:-.01em;font-size:5.95cqw;}
        .apa-price-text{display:inline-block;white-space:nowrap;line-height:1;}

        /* Echte Texte über der textfreien Bildvariante (v2); skalieren via cqw mit dem Bild */
        .apa-ov{position:absolute;color:#16356a;font-family:"Outfit",system-ui,sans-serif;pointer-events:none;}
        /* A · Notizzettel: Caveat (selbst gehostet), um -10.4° gedreht, 4 Zeilen mit Rand-Abstand */
        .apa-note{left:55.47%;top:67.71%;width:9.6%;transform:translate(-50%,-50%) rotate(-10.4deg);text-align:center;font-family:"Caveat","Outfit",cursive;color:#163a68;line-height:1.1;}
        .apa-note-price{font-weight:700;font-size:2.25cqw;}
        .apa-note-sub{font-weight:600;font-size:1.8cqw;}
        /* D · unter dem Gesamtpreis */
        .apa-netto{left:34.4%;top:72.4%;width:30%;transform:translate(-50%,-50%);text-align:center;color:#6c7693;font-weight:500;font-size:1.9cqw;}
        /* B · Spalte 3 unter dem Haken-Symbol (Stil wie Nachbarspalten).
           PRICE-03b-1d: Box exakt auf die Spaltenfläche (49.22–59.77% = 10.55%, zentriert
           54.5%) begrenzt, damit der Text nicht seitlich aus der Spalte läuft. Schrift von
           1.5cqw auf 1.3cqw reduziert: nur so passen „Gefahrstoffe und" (bei 1.5cqw 119px >
           108px Spaltenbreite) als eine Zeile und damit drei statt vier Zeilen in die Spalte.
           word-break:keep-all/overflow-wrap:normal/hyphens:none → nie Trennung mitten im Wort. */
        .apa-col3{left:54.5%;top:83.9%;width:10.55%;transform:translate(-50%,0);text-align:center;color:#223a6b;font-weight:500;letter-spacing:normal;font-size:1.3cqw;line-height:1.3;overflow-wrap:normal;word-break:keep-all;hyphens:none;}
        /* C · Fußzeile links neben dem Schild-Symbol (zwei Zeilen wie Nachbar-Fußzeilen) */
        .apa-foot1{left:12.1%;top:93.5%;width:16%;text-align:left;color:#102b7a;font-weight:600;letter-spacing:normal;font-size:1.49cqw;line-height:1.2;}
        /* 8 · Angebotshinweis (sobald 50 MA erreicht), eigene Zeile unter „netto zzgl. MwSt." */
        .apa-maxhint{left:40%;top:74.0%;width:44%;transform:translate(-50%,0);text-align:center;color:#6c7693;font-family:"Outfit",system-ui,sans-serif;font-weight:500;letter-spacing:normal;font-size:1.05cqw;line-height:1.2;}

        .apa-error{max-width:1024px;margin:12px auto 0;padding:10px 14px;background:#fef2f2;border:1px solid #fecaca;border-radius:10px;color:#b42318;font:500 .9rem/1.4 "Outfit",system-ui,sans-serif;text-align:center;}
        /* MOBIL-CTA-03, Betreiberfreigabe 27.10.2026 — Handy-Block unter dem Bild, nur bis 767 px; PC unverändert */
        .apa-mobil{display:none;}
        @media (max-width:767px){
          .apa-mobil{display:block;max-width:1024px;margin:16px auto 0;padding:16px;background:#fff;border-radius:14px;font-family:"Outfit",system-ui,sans-serif;color:#022264;}
          .apa-mobil-row{display:flex;align-items:center;justify-content:space-between;gap:12px;}
          .apa-mobil-label{font-size:16px;font-weight:600;}
          .apa-mobil-stepper{display:flex;align-items:center;gap:8px;}
          .apa-mobil-step{min-width:48px;min-height:48px;border-radius:999px;border:2px solid #022264;background:#ebf3fd;color:#022264;font-size:24px;font-weight:700;line-height:1;}
          .apa-mobil-input{width:64px;min-height:48px;text-align:center;font-size:20px;font-weight:700;border:2px solid #c9d6ea;border-radius:12px;color:#022264;background:#fff;}
          .apa-mobil-price{margin-top:12px;min-height:34px;font-size:28px;font-weight:800;text-align:center;}
          .apa-mobil-trial{display:block;width:100%;min-height:52px;margin-top:12px;border:0;border-radius:999px;background:#2f9e5b;color:#fff;font-size:18px;font-weight:700;}
          .apa-mobil-trial[disabled]{opacity:.6;}
          /* MOBIL-CTA-06, Betreiberfreigabe 27.10.2026 — Bildtexte und Download nur bis 767 px */
          .apa-mobil-h2{margin:0 0 8px;font-size:24px;line-height:1.25;font-weight:800;color:#022264;}
          .apa-mobil-sub{margin:0 0 16px;font-size:16px;line-height:1.5;color:#44506b;}
          .apa-mobil-karten{list-style:none;margin:0 0 24px;padding:0;display:grid;gap:12px;}
          .apa-mobil-karte{padding:14px 16px;border:1px solid #dde5f0;border-radius:12px;background:#f7f9fc;}
          .apa-mobil-h3{margin:0 0 4px;font-size:18px;line-height:1.3;font-weight:700;color:#022264;}
          .apa-mobil-text{margin:0;font-size:16px;line-height:1.5;color:#44506b;}
          .apa-mobil-netto{margin-top:4px;font-size:16px;text-align:center;color:#6c7693;}
          .apa-mobil-liste{list-style:none;margin:16px 0 0;padding:0;display:grid;gap:8px;font-size:16px;line-height:1.4;color:#223a6b;}
          .apa-mobil-pdf{display:flex;align-items:center;justify-content:center;box-sizing:border-box;width:100%;min-height:48px;margin-top:16px;padding:12px 16px;border:2px solid #022264;border-radius:14px;background:#fff;color:#022264;font-size:16px;font-weight:700;line-height:1.3;text-align:center;text-decoration:none;}
        }
      `}</style>

      <div className="apa-frame" data-testid="apa-frame">
        <img
          className="apa-master"
          src={MASTER}
          alt="CLartext: Preis- und Abschlussbühne mit Grundabo-Preisrechner je aktivem Mitarbeiter, 14-Tage-Test ohne Zahlungsdaten und Leistungsübersicht als PDF."
          data-testid="abo-preis-master"
          draggable="false"
        />

        {/* 1 · Trial-CTA → bestehender echter Trial-Flow */}
        <button
          type="button"
          className="apa-hot apa-hot-trial"
          data-testid="abo-trial-cta-final"
          aria-label="Jetzt kostenlos 14 Tage testen"
          onClick={startTrial}
          disabled={trialLoading}
        />

        {/* 2 · PDF-Download → echte Leistungsübersicht */}
        <a
          className="apa-hot apa-hot-pdf"
          data-testid="abo-cta-pdf"
          href={PDF}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Leistungsübersicht CLartext als PDF herunterladen"
        />

        {/* 3 · Preisrechner: eingebrannte − / + bleiben Bild (transparente Hotspots) */}
        <button
          type="button"
          className="apa-hot apa-step-minus"
          data-testid="abo-emp-minus"
          aria-label="Ein Mitarbeiter weniger"
          onClick={() => setEmp(employeeCount - 1)}
          disabled={employeeCount <= 1}
        />
        <button
          type="button"
          className="apa-hot apa-step-plus"
          data-testid="abo-emp-plus"
          aria-label="Ein Mitarbeiter mehr"
          onClick={() => {
            if (employeeCount < 50) setEmp(employeeCount + 1);
          }}
        />

        {/* Live-Mitarbeiterzahl (überdeckt die eingebrannte Zahl) */}
        <div className="apa-num apa-num-count" data-testid="abo-emp-count" aria-live="polite">
          {employeeCount}
        </div>

        {/* Live-Betriebspreis aus /api/catalog; Fläche deckt „74,50 €" in jedem priceState ab, Betrag nur bei „ready" (WERBUNG-L2-1, Betreiberfreigabe 27.10.2026) */}
        <div className="apa-num apa-num-price" data-testid="abo-price-total">
          {priceState === "ready" && (
            <span ref={priceTextRef} className="apa-price-text">
              {totalText} {currencySymbol}
            </span>
          )}
        </div>

        {/* A · Notizzettel: echter Preis aus /api/catalog (Caveat, -10.4° gedreht) */}
        {priceState === "ready" && (
          <div
            className="apa-ov apa-note"
            data-testid="abo-note-price"
            aria-hidden="true"
          >
            <div className="apa-note-price">
              {monthlyText} {currencySymbol}
            </div>
            <div className="apa-note-sub">je aktivem</div>
            <div className="apa-note-sub">Mitarbeiter</div>
            <div className="apa-note-sub">im Monat</div>
          </div>
        )}

        {/* D · unter dem Gesamtpreis */}
        <div className="apa-ov apa-netto" data-testid="abo-netto-note">
          netto zzgl. MwSt.
        </div>

        {/* B · Spalte 3 unter dem Haken-Symbol */}
        <div className="apa-ov apa-col3" data-testid="abo-col3-note">
          Gefahrstoffe und Hautschutzplan inklusive.
        </div>

        {/* C · Fußzeile links neben dem Schild-Symbol (zwei Zeilen wie Nachbarn) */}
        <div className="apa-ov apa-foot1" data-testid="abo-foot1-note">
          Orientiert an
          <br />
          ArbSchG und DGUV
        </div>

        {/* 8 · Angebotshinweis — sobald 50 MA erreicht, unter „netto zzgl. MwSt." */}
        {employeeCount >= 50 && (
          <div className="apa-ov apa-maxhint" data-testid="abo-max-hint">
            Ab 51 Mitarbeitern erstellen wir Ihnen ein individuelles Angebot.
          </div>
        )}
      </div>

      {/* MOBIL-CTA-03, Betreiberfreigabe 27.10.2026 — echte Bedienelemente am Handy, gleicher Zustand wie das Bild */}
      <div className="apa-mobil" data-testid="abo-mobil-block">
        {/* MOBIL-CTA-06, Betreiberfreigabe 27.10.2026 — oberer Bildteil als echter Text */}
        <h2 className="apa-mobil-h2" data-testid="abo-mobil-titel">{MOBIL_TITEL}</h2>
        <p className="apa-mobil-sub" data-testid="abo-mobil-untertitel">{MOBIL_UNTERTITEL}</p>
        <ul className="apa-mobil-karten" data-testid="abo-mobil-karten">
          {MOBIL_KARTEN.map(([titel, text], i) => (
            <li key={titel} className="apa-mobil-karte" data-testid={`abo-mobil-karte-${i + 1}`}>
              <h3 className="apa-mobil-h3">{titel}</h3>
              <p className="apa-mobil-text">{text}</p>
            </li>
          ))}
        </ul>
        <h2 className="apa-mobil-h2" data-testid="abo-mobil-einfach">CLARTEXT macht es einfach.</h2>
        <div className="apa-mobil-row">
          <label className="apa-mobil-label" htmlFor="abo-mobil-emp">Mitarbeiter</label>
          <div className="apa-mobil-stepper">
            <button
              type="button"
              className="apa-mobil-step"
              data-testid="abo-mobil-emp-minus"
              aria-label="Ein Mitarbeiter weniger"
              onClick={() => {
                if (employeeCount > 1) setEmp(employeeCount - 1);
              }}
            >
              −
            </button>
            <input
              id="abo-mobil-emp"
              className="apa-mobil-input"
              data-testid="abo-mobil-emp-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={empLeer ? "" : String(employeeCount)}
              onChange={(e) => {
                const roh = e.target.value.trim();
                if (roh === "") return setEmpLeer(true);
                if (!/^\d+$/.test(roh)) return;
                setEmpLeer(false);
                setEmp(Math.max(1, Math.min(50, parseInt(roh, 10))));
              }}
              onBlur={() => setEmpLeer(false)}
            />
            <button
              type="button"
              className="apa-mobil-step"
              data-testid="abo-mobil-emp-plus"
              aria-label="Ein Mitarbeiter mehr"
              onClick={() => {
                if (employeeCount < 50) setEmp(employeeCount + 1);
              }}
            >
              +
            </button>
          </div>
        </div>
        <div className="apa-mobil-price" data-testid="abo-mobil-price" aria-live="polite">
          {priceState === "ready" && (
            <>
              {totalText} {currencySymbol}
            </>
          )}
        </div>
        {priceState === "ready" && (
          <div className="apa-mobil-netto" data-testid="abo-mobil-netto">netto zzgl. MwSt.</div>
        )}
        <button
          type="button"
          className="apa-mobil-trial"
          data-testid="abo-mobil-trial"
          onClick={startTrial}
          disabled={trialLoading}
        >
          Jetzt kostenlos 14 Tage testen
        </button>
        <ul className="apa-mobil-liste" data-testid="abo-mobil-vertrauen">
          {MOBIL_VERTRAUEN.map((zeile) => <li key={zeile}>{zeile}</li>)}
        </ul>
        <ul className="apa-mobil-liste" data-testid="abo-mobil-merkmale">
          {MOBIL_MERKMALE.map((zeile) => <li key={zeile}>{zeile}</li>)}
        </ul>
        <a
          className="apa-mobil-pdf"
          data-testid="abo-mobil-pdf"
          href={PDF}
          target="_blank"
          rel="noopener noreferrer"
        >
          Leistungsübersicht CLartext als PDF herunterladen
        </a>
      </div>

      {trialError && (
        <p className="apa-error" role="alert" data-testid="abo-trial-error">
          {trialError}
        </p>
      )}
    </section>
  );
}
